'use client';

import dynamic from 'next/dynamic';
import Link from 'next/link';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import FilterBar from '@/components/FilterBar';
import HomeInfoPanel from '@/components/HomeInfoPanel';
import NearbyCard from '@/components/NearbyCard';
import type { RouteInfo } from '@/components/NavPanel';
import TopBar from '@/components/TopBar';
import { featureToCharger, fetchAuthMe, fetchGeojson, fetchOperators, fetchProposals, fetchStats, fetchStatusChanges, geocodeForward, geocodeReverse, type AuthMe, type ChargerFC, type RoutePlanResult } from '@/lib/api';
import type { FeatureCollection } from 'geojson';
import { loadFilters, matchesOperator, persistFilters } from '@/lib/filters';
import { bearingDeg, haversineM } from '@/lib/geo';
import { INITIAL_FILTERS, type Charger, type FilterState, type HeatmapSubMode, type CpoFilterGroup } from '@/types/charger';

const EVMap = dynamic(() => import('@/components/EVMap'), { ssr: false });
const ChargerSheet = dynamic(() => import('@/components/ChargerSheet'), { ssr: false });
const NavPanel = dynamic(() => import('@/components/NavPanel'), { ssr: false });
const ProposeChargerModal = dynamic(() => import('@/components/ProposeChargerModal'), { ssr: false });
const ProposalSheet = dynamic(() => import('@/components/ProposalSheet'), { ssr: false });
const WelcomeGuideModal = dynamic(() => import('@/components/WelcomeGuideModal'), { ssr: false });
const LiveChargePanel = dynamic(() => import('@/components/LiveChargePanel'), { ssr: false });
const RoutePlannerPanel = dynamic(() => import('@/components/RoutePlannerPanel'), { ssr: false });

function applyFilters(fc: ChargerFC, f: FilterState): ChargerFC {
  return {
    type: 'FeatureCollection',
    features: fc.features.filter((feat) => {
      const p = feat.properties;
      if (f.minKw > 0 && p.maxKw < f.minKw) return false;
      if (f.onlyAvailable && p.status !== 'available') return false;
      if (f.operator && !matchesOperator(f.operator, p.operatorCode)) return false;
      if (f.wcGas5Min && !p.wcGas5Min) return false;
      const cons = Array.isArray(p.connectors) ? p.connectors : [];
      if (f.connectors.length && !f.connectors.some((c) => cons.includes(c))) return false;
      return true;
    }),
  };
}

export default function HomeApp({
  initialActiveCount = 0,
  infoContent,
}: {
  initialActiveCount?: number;
  infoContent: ReactNode;
}) {
  const [raw, setRaw] = useState<ChargerFC>({ type: 'FeatureCollection', features: [] });
  const [filters, setFilters] = useState<FilterState>(INITIAL_FILTERS);
  const [filtersReady, setFiltersReady] = useState(false);
  const [ops, setOps] = useState<Array<{ organization_code: string; name: string }>>([]);
  // Szerver oldalon (SSR) lekért, friss darabszámmal indul, hogy ne látszódjon
  // "0+ hazai töltőpont" a betöltés első pillanatában (üres/hibás tartalom benyomás).
  const [active, setActive] = useState(initialActiveCount);
  const [selected, setSelected] = useState<Charger | null>(null);
  const [flyTo, setFlyTo] = useState<{ lng: number; lat: number; zoom?: number } | null>(null);
  const [proposals, setProposals] = useState<FeatureCollection>({ type: 'FeatureCollection', features: [] });
  const [proposeAt, setProposeAt] = useState<{ lng: number; lat: number } | null>(null);
  const [selectedProposal, setSelectedProposal] = useState<number | null>(null);
  const [loggedIn, setLoggedIn] = useState(false);
  const [me, setMe] = useState<AuthMe | null>(null);
  const [routePlannerOpen, setRoutePlannerOpen] = useState(false);
  const [routePlan, setRoutePlan] = useState<{ geometry: NonNullable<RoutePlanResult['geometry']>; waypoints: [number, number][] } | null>(null);
  const [suggestedChargers, setSuggestedChargers] = useState<Charger[]>([]);
  const [origin, setOrigin] = useState<{ lng: number; lat: number; source: 'gps' | 'map' } | null>(null);
  const [mapCenter, setMapCenter] = useState<{ lng: number; lat: number }>({ lng: 19.0402, lat: 47.4979 });
  const [settlement, setSettlement] = useState('');
  const [route, setRoute] = useState<{ from: [number, number]; to: [number, number]; destName: string; waypoints?: [number, number][] } | null>(null);
  const [routeInfo, setRouteInfo] = useState<RouteInfo | null>(null);
  const [nav3d, setNav3d] = useState(true);
  const [heading, setHeading] = useState<number | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [introOpen, setIntroOpen] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [mapStyle, setMapStyle] = useState<'vector' | 'satellite' | 'heatmap'>('vector');
  const [heatmapSubMode, setHeatmapSubMode] = useState<HeatmapSubMode>('coverage');
  const [cpoFilter, setCpoFilter] = useState<CpoFilterGroup>('all');

  const cpoCounts = useMemo(() => {
    const counts: Record<string, { count: number; evseTotal: number }> = {
      all: { count: 0, evseTotal: 0 },
      mobiliti: { count: 0, evseTotal: 0 },
      mol_plugee: { count: 0, evseTotal: 0 },
      tea: { count: 0, evseTotal: 0 },
      eon: { count: 0, evseTotal: 0 },
      tesla: { count: 0, evseTotal: 0 },
      ionity: { count: 0, evseTotal: 0 },
      omv: { count: 0, evseTotal: 0 },
      other: { count: 0, evseTotal: 0 },
    };
    if (!raw?.features) return counts;
    for (const f of raw.features) {
      const group = ((f.properties?.cpoGroup as string) || 'other').toLowerCase();
      const evses = Number(f.properties?.evseCount || 1);
      counts.all.count++;
      counts.all.evseTotal += evses;
      if (counts[group]) {
        counts[group].count++;
        counts[group].evseTotal += evses;
      } else {
        counts.other.count++;
        counts.other.evseTotal += evses;
      }
    }
    return counts;
  }, [raw]);
  const [mapAction, setMapAction] = useState<{ type: 'zoomIn' | 'zoomOut' | 'resetNorth'; t: number } | null>(null);
  const [mapReady, setMapReady] = useState(false);

  const handleDismissGuidePermanently = () => {
    try {
      localStorage.setItem('evtolto_guide_dismissed_v1', 'true');
    } catch {
      /* ignore */
    }
    setShowGuide(false);
  };
  const [poiActive, setPoiActive] = useState(false);

  const topStackRef = useRef<HTMLDivElement>(null);
  const [topStackHeight, setTopStackHeight] = useState(0);
  const sinceRef = useRef('');
  const loadedRef = useRef(false);
  const originRef = useRef(origin);
  originRef.current = origin;
  // A ChargerSheet chunkját (és mountját) az első kiválasztásig halasztjuk; utána
  // mountolva marad, hogy a belső állapota (pl. aktív töltés) ugyanúgy megmaradjon.
  const sheetUsedRef = useRef(false);
  if (selected) sheetUsedRef.current = true;

  // A címsor és a közeli töltők a fejléc (kereső + szűrők) alá kerülnek.
  // A fejléc magassága változik, ezért a fix top-offset helyett mérjük.
  useLayoutEffect(() => {
    const el = topStackRef.current;
    if (!el) return;
    const measure = () => setTopStackHeight(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // A térkép indulása után, tétlen időben előtöltjük a ChargerSheet chunkot,
  // hogy az első töltőre kattintáskor ne legyen érezhető késés.
  useEffect(() => {
    if (!mapReady) return;
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    const load = () => {
      void import('@/components/ChargerSheet');
    };
    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(load, { timeout: 5000 });
      return () => w.cancelIdleCallback?.(id);
    }
    const t = window.setTimeout(load, 3000);
    return () => window.clearTimeout(t);
  }, [mapReady]);

  // Stabil referencia: a ChargerSheet polling effektje függ tőle, inline
  // függvénnyel minden HomeApp renderkor újraindulna az intervallum.
  const handleSheetStatus = useCallback(
    (id: number, status: Charger['status'], live?: boolean, evseCount?: number, evseOccupied?: number, evseAvailable?: number) => {
      setSelected((sel) =>
        sel && sel.id === id
          ? {
              ...sel,
              status,
              ...(live !== undefined ? { live } : {}),
              ...(evseCount !== undefined ? { evseCount, evseOccupied, evseAvailable } : {}),
            }
          : sel
      );
      setRaw((prev) => ({
        type: 'FeatureCollection',
        features: prev.features.map((f) =>
          f.properties.id === id
            ? {
                ...f,
                properties: {
                  ...f.properties,
                  status,
                  ...(live !== undefined ? { live } : {}),
                  ...(evseCount !== undefined ? { evseCount, evseOccupied, evseAvailable } : {}),
                },
              }
            : f
        ),
      }));
    },
    []
  );

  useEffect(() => {
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    const start = () => setMapReady(true);
    const p = new URLSearchParams(window.location.search);
    if (p.get('charger') || p.get('lat') || p.get('lng')) {
      start();
      return;
    }
    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(start, { timeout: 400 });
      return () => w.cancelIdleCallback?.(id);
    }
    const t = window.setTimeout(start, 0);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    setFilters(loadFilters());
    setFiltersReady(true);
    const onPop = () => setFilters(loadFilters());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    if (!filtersReady) return;
    persistFilters(filters);
  }, [filters, filtersReady]);

  useEffect(() => {
    fetchAuthMe().then((u) => {
      setLoggedIn(Boolean(u));
      setMe(u);
    });

    const deferTimer = setTimeout(() => {
      fetchOperators().then(setOps).catch(() => undefined);
      fetchStats()
        .then((s) => setActive(s.chargers_active))
        .catch(() => undefined);
      fetchProposals().then(setProposals).catch(() => undefined);
    }, 1200);

    return () => clearTimeout(deferTimer);
  }, []);

  useEffect(() => {
    if (!mapReady) return;
    fetchGeojson().then((fc) => {
      setRaw(fc);
      loadedRef.current = true;
      const params = new URLSearchParams(window.location.search);
      const id = Number(params.get('charger') || 0);
      if (id > 0) {
        const feat = fc.features.find((x) => Number(x.properties.id) === id);
        if (feat) {
          setSelected(featureToCharger(feat));
          const coords = feat.geometry.coordinates;
          setFlyTo({ lng: Number(coords[0]), lat: Number(coords[1]), zoom: 15 });
        }
      } else {
        const lat = Number(params.get('lat'));
        const lng = Number(params.get('lng'));
        if (Number.isFinite(lat) && Number.isFinite(lng) && lat !== 0 && lng !== 0) {
          setFlyTo({ lng, lat, zoom: Number(params.get('zoom')) || 13 });
        } else {
          // Városi és folyosó-linkek (?q=Debrecen). A hálózatok külön, ?cpo= kóddal szűrnek.
          const q = params.get('q');
          if (q && q.trim()) {
            geocodeForward(q.trim()).then((hit) => {
              if (hit?.lat != null && hit?.lng != null) {
                setFlyTo({ lng: hit.lng, lat: hit.lat, zoom: 12 });
                setSettlement(hit.settlement || q.trim());
              }
            }).catch(() => undefined);
          }
        }
      }
    }).catch(() => undefined);
  }, [mapReady]);

  useEffect(() => {
    let timer: number | undefined;
    const tick = async () => {
      if (!loadedRef.current) return;
      try {
        const { ts, changes } = await fetchStatusChanges(sinceRef.current);
        if (ts) sinceRef.current = ts;
        if (!changes.length) return;
        const byId = new Map(changes.map((c) => [c.id, c]));
        setRaw((prev) => {
          let any = false;
          const features = prev.features.map((f) => {
            const ch = byId.get(f.properties.id);
            if (!ch) return f;
            if (
              f.properties.status === ch.status &&
              f.properties.evseOccupied === ch.evseOccupied &&
              f.properties.evseAvailable === ch.evseAvailable
            )
              return f;
            any = true;
            return {
              ...f,
              properties: {
                ...f.properties,
                status: ch.status,
                live: ch.live,
                evseCount: ch.evseCount !== undefined ? ch.evseCount : f.properties.evseCount,
                evseOccupied: ch.evseOccupied !== undefined ? ch.evseOccupied : f.properties.evseOccupied,
                evseAvailable: ch.evseAvailable !== undefined ? ch.evseAvailable : f.properties.evseAvailable,
              },
            };
          });
          return any ? { type: 'FeatureCollection', features } : prev;
        });
        setSelected((sel) => {
          if (!sel) return sel;
          const ch = byId.get(sel.id);
          if (!ch || sel.status === ch.status) return sel;
          return { ...sel, status: ch.status, live: ch.live };
        });
      } catch {
        /* keep map as-is */
      }
    };
    timer = window.setInterval(tick, 8000);
    return () => window.clearInterval(timer);
  }, []);

  const filtered = useMemo(() => applyFilters(raw, filters), [raw, filters]);

  useEffect(() => {
    if (!mapCenter) return;
    const t = window.setTimeout(() => {
      void geocodeReverse(mapCenter.lat, mapCenter.lng).then((hit) => {
        setSettlement(hit?.settlement || '');
      });
    }, 400);
    return () => window.clearTimeout(t);
  }, [mapCenter.lat, mapCenter.lng]);

  useEffect(() => {
    if (!route) return;
    if (!navigator.geolocation) return;
    let last: { lng: number; lat: number } | null = originRef.current;
    const watch = navigator.geolocation.watchPosition(
      (pos) => {
        const lng = pos.coords.longitude;
        const lat = pos.coords.latitude;
        setOrigin({ lng, lat, source: 'gps' });
        const h = pos.coords.heading;
        if (h != null && Number.isFinite(h) && h >= 0) {
          setHeading(h);
        } else if (last && haversineM(last.lat, last.lng, lat, lng) > 4) {
          setHeading(bearingDeg(last.lat, last.lng, lat, lng));
        }
        last = { lng, lat };
        setRoute((prev) => (prev ? { ...prev, from: [lng, lat] } : prev));
      },
      () => undefined,
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 12000 }
    );
    return () => navigator.geolocation.clearWatch(watch);
  }, [Boolean(route)]);

  const startNav = (c: Charger) => {
    const to: [number, number] = [c.coordinates[0], c.coordinates[1]];
    const go = (lng: number, lat: number) => {
      setSelected(c);
      setSelectedProposal(null);
      setNav3d(true);
      setRoute({ from: [lng, lat], to, destName: c.name || c.settlement });
      setRouteInfo({ distanceM: 0, durationS: 0, destName: c.name || c.settlement, loading: true });
    };
    const cur = originRef.current;
    if (cur) {
      go(cur.lng, cur.lat);
      return;
    }
    if (!navigator.geolocation) {
      go(19.0402, 47.4979);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lng = pos.coords.longitude;
        const lat = pos.coords.latitude;
        setOrigin({ lng, lat, source: 'gps' });
        go(lng, lat);
      },
      () => go(19.0402, 47.4979),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const startRoutePlanNav = (points: [number, number][], destName: string) => {
    if (points.length < 2) return;
    const from = points[0];
    const to = points[points.length - 1];
    const waypoints = points.slice(1, -1);
    setRoutePlannerOpen(false);
    setRoutePlan(null);
    setSuggestedChargers([]);
    setSelected(null);
    setSelectedProposal(null);
    setNav3d(true);
    setRoute({ from, to, destName, waypoints });
    setRouteInfo({ distanceM: 0, durationS: 0, destName, loading: true });
  };

  return (
    <div className="home-map-app relative h-full min-h-0 w-full overflow-hidden bg-slate-900">
      {/* 1. Full-screen Map as default background */}
      <div className="absolute inset-0 z-0">
        {mapReady ? (
          <EVMap
            data={filtered}
            selectedId={selected?.id ?? null}
            selectedCharger={selected}
            poiActive={poiActive}
            onTogglePoi={() => setPoiActive((v) => !v)}
            onSelect={(c) => {
              setSelectedProposal(null);
              setSelected(c);
            }}
            flyTo={flyTo}
            proposals={proposals}
            onSelectProposal={(id) => {
              setSelected(null);
              setSelectedProposal(id);
            }}
            onProposeAt={(lng, lat) => {
              if (!loggedIn) {
                window.location.href = '/account/login.php?next=/';
                return;
              }
              setProposeAt({ lng, lat });
            }}
            route={route}
            onRouteInfo={setRouteInfo}
            routePlan={routePlan}
            suggestedChargers={suggestedChargers}
            onOrigin={(lng, lat, source) => setOrigin({ lng, lat, source })}
            onMapCenter={(lng, lat) => setMapCenter({ lng, lat })}
            nav3d={nav3d && !!route}
            heading={heading}
            mapStyle={mapStyle}
            onMapStyleChange={setMapStyle}
            heatmapSubMode={heatmapSubMode}
            onHeatmapSubModeChange={setHeatmapSubMode}
            cpoFilter={cpoFilter}
            onCpoFilterChange={setCpoFilter}
            mapAction={mapAction}
          />
        ) : (
          <div className="absolute inset-0 bg-slate-900" aria-hidden="true" />
        )}
      </div>

      {/* 2. Top Controls (Search & Desktop Quick Filters) */}
      <div ref={topStackRef} className="pointer-events-none absolute inset-x-0 top-0 z-30 flex w-full flex-col">
        <TopBar
          activeCount={active}
          onFlyTo={(lng, lat, zoom) => {
            setMapReady(true);
            setFlyTo({ lng, lat, zoom });
          }}
          onPickOperator={(code) => {
            setMapReady(true);
            setFilters((f) => ({ ...f, operator: code }));
          }}
          onPickCharger={(id, lng, lat) => {
            setMapReady(true);
            const feat = raw.features.find((x) => x.properties.id === id);
            if (feat) setSelected(featureToCharger(feat));
            setFlyTo({ lng, lat, zoom: 15 });
          }}
          onSearchOpen={(open) => {
            if (open) setMapReady(true);
            setSearchOpen(open);
          }}
          onOpenInfo={() => setInfoOpen(true)}
          onOpenGuide={() => {
            setMapReady(true);
            setShowGuide(true);
          }}
          onOpenRoutePlanner={() => {
            setMapReady(true);
            setRoutePlannerOpen(true);
          }}
        />
        <FilterBar
          filters={filters}
          onFilterChange={(f) => {
            setMapReady(true);
            setFilters(f);
          }}
          operators={ops}
          mapStyle={mapStyle}
          onMapStyleChange={setMapStyle}
          heatmapSubMode={heatmapSubMode}
          onHeatmapSubModeChange={setHeatmapSubMode}
          cpoFilter={cpoFilter}
          onCpoFilterChange={setCpoFilter}
          cpoCounts={cpoCounts}
          onZoomIn={() => setMapAction({ type: 'zoomIn', t: Date.now() })}
          onZoomOut={() => setMapAction({ type: 'zoomOut', t: Date.now() })}
          onResetNorth={() => setMapAction({ type: 'resetNorth', t: Date.now() })}
          onLocate={() => {
            if (!navigator.geolocation) return;
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                const lng = pos.coords.longitude;
                const lat = pos.coords.latitude;
                setOrigin({ lng, lat, source: 'gps' });
                setFlyTo({ lng, lat, zoom: 15 });
              },
              (err) => console.warn('Geolocation error:', err),
              { enableHighAccuracy: true, timeout: 8000 }
            );
          }}
          selectedCharger={selected}
          poiActive={poiActive}
          onTogglePoi={() => setPoiActive((v) => !v)}
        />
      </div>

      <div className="pointer-events-none absolute inset-0 z-10">
        <div
          className="pointer-events-none absolute left-3 z-10 flex max-w-[calc(100%-5rem)] flex-col items-start gap-2 md:left-6"
          style={{ top: topStackHeight + 4 }}
        >
          <div
            className={`pointer-events-auto max-w-xl rounded-xl border border-white/80 bg-white/90 px-3 py-1.5 text-slate-900 shadow-md backdrop-blur-md ${
              searchOpen ? 'hidden' : 'block'
            } ${introOpen ? 'w-full' : 'w-max max-w-full'}`}
          >
            <button
              type="button"
              className="flex w-full items-center justify-between gap-2 text-left"
              aria-expanded={introOpen}
              onClick={() => setIntroOpen((v) => !v)}
            >
              <h1 className="text-xs font-extrabold leading-tight tracking-tight sm:text-sm">
                Elektromos autótöltő térkép Magyarországon
              </h1>
              {introOpen ? (
                <ChevronUp className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
              ) : (
                <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
              )}
            </button>
            {introOpen ? (
              <p className="mt-0.5 text-[10px] leading-snug text-slate-600 sm:text-xs">
                Keress töltőt helyszín, csatlakozó és teljesítmény szerint; ahol elérhető, az aktuális
                foglaltságot is megmutatjuk.
              </p>
            ) : null}
          </div>
          <div
            className={`w-full max-w-[20rem] flex-col gap-2 ${
              searchOpen ? 'hidden md:flex' : 'flex'
            }`}
          >
            {route && routeInfo ? (
              <NavPanel
                info={routeInfo}
                nav3d={nav3d}
                live={origin?.source === 'gps'}
                onToggle3d={() => setNav3d((v) => !v)}
                onClose={() => {
                  setRoute(null);
                  setRouteInfo(null);
                  setNav3d(true);
                }}
              />
            ) : null}
            <NearbyCard
              data={filtered}
              origin={mapCenter}
              settlement={settlement}
              onSelect={(c) => {
                setMapReady(true);
                setSelectedProposal(null);
                setSelected(c);
                setFlyTo({ lng: c.coordinates[0], lat: c.coordinates[1], zoom: 15 });
              }}
              onNavigate={(c) => {
                setMapReady(true);
                startNav(c);
              }}
            />
          </div>
        </div>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 top-[8%] z-40 flex min-h-0 flex-col md:inset-auto md:bottom-4 md:right-16 md:top-auto md:block md:h-auto md:max-h-[calc(100dvh-7rem)] md:w-[380px]">
          {selectedProposal != null ? (
            <ProposalSheet
              id={selectedProposal}
              onClose={() => setSelectedProposal(null)}
              onChanged={() => {
                fetchProposals().then(setProposals).catch(() => undefined);
                fetchGeojson().then(setRaw).catch(() => undefined);
              }}
            />
          ) : sheetUsedRef.current ? (
          <ChargerSheet
            charger={selected}
            onClose={() => {
              setSelected(null);
            }}
            poiActive={poiActive}
            onTogglePoi={() => setPoiActive((v) => !v)}
            onNavigate={startNav}
            onStatus={handleSheetStatus}
          />
          ) : null}
        </div>
      </div>

      {routePlannerOpen ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 top-[8%] z-40 flex min-h-0 flex-col md:inset-auto md:bottom-4 md:left-4 md:top-auto md:block md:h-auto md:max-h-[calc(100dvh-7rem)]">
          <RoutePlannerPanel
            open={routePlannerOpen}
            onClose={() => {
              setRoutePlannerOpen(false);
              setRoutePlan(null);
              setSuggestedChargers([]);
            }}
            data={filtered}
            me={me}
            operators={ops}
            onPlanned={setRoutePlan}
            onSuggestedChargersChange={setSuggestedChargers}
            onStartNavigation={startRoutePlanNav}
            onSelectCharger={(c) => {
              setMapReady(true);
              setSelectedProposal(null);
              setSelected(c);
              setFlyTo({ lng: c.coordinates[0], lat: c.coordinates[1], zoom: 15 });
            }}
          />
        </div>
      ) : null}

      {loggedIn ? <LiveChargePanel enabled /> : null}

      {/* Floating Bottom Navigation Menu (subpages) */}
      {!selected && !selectedProposal && !route ? (
        <nav
          className="pointer-events-auto absolute inset-x-0 bottom-3 z-20 flex justify-center px-2 md:bottom-5"
          aria-label="Menü"
        >
          <div className="no-scrollbar flex max-w-full items-center gap-0.5 overflow-x-auto rounded-full border border-white/10 bg-slate-900/95 px-1.5 py-1.5 shadow-xl backdrop-blur">
            <Link
              href="/rolunk"
              title="Rólunk & Hogyan működik?"
              className="flex flex-col items-center gap-0.5 rounded-full px-3 py-1 text-[11px] font-semibold text-slate-200 transition hover:bg-emerald-600 hover:text-white"
            >
              <span className="text-base leading-none" aria-hidden="true">ℹ️</span>
              <span>Rólunk</span>
            </Link>
            <Link
              href="/gyik"
              title="Gyakori kérdések"
              className="flex flex-col items-center gap-0.5 rounded-full px-3 py-1 text-[11px] font-semibold text-slate-200 transition hover:bg-emerald-600 hover:text-white"
            >
              <span className="text-base leading-none" aria-hidden="true">❓</span>
              <span>GYIK</span>
            </Link>
            <Link
              href="/halozatok"
              title="Legnagyobb töltőhálózatok"
              className="flex flex-col items-center gap-0.5 rounded-full px-3 py-1 text-[11px] font-semibold text-slate-200 transition hover:bg-emerald-600 hover:text-white"
            >
              <span className="text-base leading-none" aria-hidden="true">🔌</span>
              <span>Hálózatok</span>
            </Link>
            <Link
              href="/toltok"
              title="Töltők városonként"
              className="flex flex-col items-center gap-0.5 rounded-full px-2.5 py-1 text-[11px] font-semibold text-slate-200 transition hover:bg-emerald-600 hover:text-white"
            >
              <span className="text-base leading-none" aria-hidden="true">🏙️</span>
              <span>Városok</span>
            </Link>
            <Link
              href="/utmutato"
              title="Szerkesztett útmutatók"
              className="flex flex-col items-center gap-0.5 rounded-full px-2.5 py-1 text-[11px] font-semibold text-slate-200 transition hover:bg-emerald-600 hover:text-white"
            >
              <span className="text-base leading-none" aria-hidden="true">📘</span>
              <span>Útmutató</span>
            </Link>
            <a
              href="#tovabbi"
              title="Adatforrások és szerkesztett tartalom a térkép alatt"
              className="flex flex-col items-center gap-0.5 rounded-full px-2.5 py-1 text-[11px] font-semibold text-slate-200 transition hover:bg-emerald-600 hover:text-white"
            >
              <span className="text-base leading-none" aria-hidden="true">↓</span>
              <span>Tartalom</span>
            </a>
          </div>
        </nav>
      ) : null}

      {/* Propose Charger Modal (Top Level with pointer-events-auto) */}
      {proposeAt ? (
        <ProposeChargerModal
          lng={proposeAt.lng}
          lat={proposeAt.lat}
          onClose={() => setProposeAt(null)}
          onCreated={() => {
            setProposeAt(null);
            fetchProposals().then(setProposals).catch(() => undefined);
          }}
        />
      ) : null}

      {/* Welcome & Features Guide Modal (Top Level with pointer-events-auto) */}
      {showGuide ? (
        <WelcomeGuideModal
          isOpen
          onClose={() => setShowGuide(false)}
          onDismissPermanently={handleDismissGuidePermanently}
        />
      ) : null}

      <HomeInfoPanel open={infoOpen} onClose={() => setInfoOpen(false)}>
        {infoContent}
      </HomeInfoPanel>
    </div>
  );
}
